import api from './api';

export const getCart = async () => {
  const { data } = await api.get('/cart');
  return data;
};

export const addToCart = async (payload) => {
  const { data } = await api.post('/cart', payload);
  return data;
};

export const updateCartItem = async (payload) => {
  const { data } = await api.put('/cart', payload);
  return data;
};

export const clearCart = async () => {
  const { data } = await api.delete('/cart');
  return data;
};

// ============================================================
// Checkout
//
//   getCheckoutQuote()      -> exact totals, delivery date, COD availability
//                              (calculated on the server; the browser never
//                              decides the price)
//   Online: createRazorpayOrder() -> Razorpay popup -> verifyRazorpayPayment()
//   COD:    placeCodOrder()
// ============================================================
export const getCheckoutConfig = async () => {
  const { data } = await api.get('/orders/checkout-config');
  return data;
};

export const getCheckoutQuote = async ({ addressId, couponCode, paymentMethod }) => {
  const { data } = await api.post('/orders/quote', { addressId, couponCode, paymentMethod });
  return data;
};

export const createRazorpayOrder = async ({ addressId, couponCode }) => {
  const { data } = await api.post('/orders/razorpay/order', { addressId, couponCode });
  return data;
};

export const verifyRazorpayPayment = async (payload) => {
  const { data } = await api.post('/orders/razorpay/verify', payload);
  return data;
};

export const placeCodOrder = async ({ addressId, couponCode }) => {
  const { data } = await api.post('/orders/cod', { addressId, couponCode });
  return data;
};

export const getMyOrders = async () => {
  const { data } = await api.get('/orders/myorders');
  return data;
};

export const getAllOrders = async (params = {}) => {
  const pageSize = params.limit || 100;
  const { data } = await api.get('/orders', {
    params: { ...params, page: params.page || 1, limit: pageSize }
  });

  if (Array.isArray(data)) return data;

  const orders = Array.isArray(data?.orders) ? [...data.orders] : [];
  const pages = Number(data?.pages) || 1;

  // An explicit page means "this page only". Otherwise walk every page so
  // the orders screen, customer counts, and reports still receive the full list.
  if (params.page || pages <= 1) return orders;

  for (let page = 2; page <= pages; page += 1) {
    const next = await api.get('/orders', { params: { ...params, page, limit: pageSize } });
    const chunk = next.data?.orders;
    if (Array.isArray(chunk)) orders.push(...chunk);
  }
  return orders;
};

export const updateOrderStatus = async (id, payload) => {
  const { data } = await api.put(`/orders/${id}`, payload);
  return data;
};

export const cancelOrder = async (id, reason) => {
  const { data } = await api.put(`/orders/${id}/cancel`, { reason });
  return data;
};

export const deleteMyOrder = async (id) => {
  const { data } = await api.delete(`/orders/my/${id}`);
  return data;
};

export const clearMyOrders = async () => {
  const { data } = await api.delete('/orders/my');
  return data;
};

export const deleteOrder = async (id) => {
  const { data } = await api.delete(`/orders/${id}`);
  return data;
};

// Public tracking needs the order ID and the last 4 digits of the phone number
export const getOrderByOrderId = async (orderId, phone) => {
  const { data } = await api.get(`/orders/track/${encodeURIComponent(orderId)}`, {
    params: { phone }
  });
  return data;
};

// ---------- Invoices, tracking, returns ----------
const saveBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

// Errors from a blob request come back as a Blob — turn them into a message
const blobErrorMessage = async (err, fallback) => {
  try {
    const text = await err?.response?.data?.text?.();
    return JSON.parse(text).message || fallback;
  } catch (e) {
    return err?.response?.data?.message || fallback;
  }
};

export const downloadOrderInvoice = async (order) => {
  try {
    const response = await api.get(`/orders/${order._id}/invoice`, { responseType: 'blob' });
    saveBlob(new Blob([response.data], { type: 'application/pdf' }), `Invoice_${order.orderId || order._id}.pdf`);
  } catch (err) {
    throw new Error(await blobErrorMessage(err, 'Could not download the invoice'));
  }
};

export const refreshOrderTracking = async (id) => {
  const { data } = await api.post(`/orders/${id}/tracking/refresh`);
  return data;
};

export const requestReturn = async (id, { reason, comments, upiId }) => {
  const { data } = await api.post(`/orders/${id}/return`, { reason, comments, upiId });
  return data;
};

// ---------- Admin fulfilment ----------
export const shipOrder = async (id, payload = {}) => {
  const { data } = await api.post(`/orders/${id}/ship`, payload);
  return data;
};

export const editDelhiveryShipment = async (id, payload = {}) => {
  const { data } = await api.post(`/orders/${id}/shipment/edit`, payload);
  return data;
};

export const cancelDelhiveryShipment = async (id) => {
  const { data } = await api.post(`/orders/${id}/shipment/cancel`);
  return data;
};

export const updateDelhiveryEwaybill = async (id, payload) => {
  const { data } = await api.put(`/orders/${id}/shipment/ewaybill`, payload);
  return data;
};

export const generateDelhiveryLabel = async (id, payload = {}) => {
  const { data } = await api.post(`/orders/${id}/shipment/label`, payload);
  return data;
};

export const manageReturn = async (id, payload) => {
  const { data } = await api.put(`/orders/${id}/return`, payload);
  return data;
};

// ---------- Delivery check (product page / checkout) ----------
export const getDelhiveryWarehouse = async () => {
  const { data } = await api.get('/shipping/delhivery/warehouse');
  return data;
};

export const createDelhiveryWarehouse = async (payload) => {
  const { data } = await api.post('/shipping/delhivery/warehouse', payload);
  return data;
};

export const updateDelhiveryWarehouse = async (payload) => {
  const { data } = await api.put('/shipping/delhivery/warehouse', payload);
  return data;
};

export const createDelhiveryPickup = async (payload) => {
  const { data } = await api.post('/shipping/delhivery/pickup', payload);
  return data;
};

export const requestDelhiveryPickup = async (id, payload = {}) => {
  const { data } = await api.post(`/orders/${id}/shipment/pickup`, payload);
  return data;
};

export const getDelhiveryCharges = async (params = {}) => {
  const { data } = await api.get('/shipping/delhivery/charges', { params });
  return data;
};

export const checkDelivery = async (pincode, { cod = false, qty = 1 } = {}) => {
  const { data } = await api.get('/shipping/serviceability', {
    params: { pincode, cod: cod ? 1 : 0, qty }
  });
  return data;
};

export const downloadOrdersExcel = async (params = {}) => {
  const response = await api.get('/orders/download', {
    params,
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'orders.xlsx');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

// Downloads the "Order Report" PDF (Order ID / Customer / Items / Payment /
// Status / Total / Date). Same blob-download pattern as the Excel export
// above, and accepts the same params (search/date/month) so the PDF export
// matches whatever's currently searched/filtered on screen — mirrors
// downloadProductsPdf's behavior in productService.js.
export const downloadOrdersPdf = async (params = {}) => {
  const response = await api.get('/orders/download-pdf', {
    params,
    responseType: 'blob'
  });
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', 'orders.pdf');
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};